import React, { useEffect, useRef, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import os from './Orders.module.css'
import { apiJson } from '../../lib/api'
import { useLayer, useChanged } from '../../components/layers'
import { PendingButton, inertClass, useSlow } from '../../components/pending'
import { digits } from './model'
import { DateField } from '../../components/DateField'
import {
  MATERIAL_NOTE,
  MODES,
  PRICE_ERROR,
  VALID_NOTE,
  blankEditor,
  blankLine,
  canRemoveLine,
  classText,
  editorBody,
  editorFrom,
  editorValid,
  lastPriceFor,
  money,
  namesWithCurrent,
  priceHint,
  priceValid,
  qtyError,
  staleNote,
  todayIso,
  withCurrent,
  withProduct,
  type EditorLine,
  type EditorOptions,
  type EditorState,
  type OrderRecord,
} from './orders'

// The order editor (V4 order editor; handoff §7n.3). One form for New order
// and for editing: the whole order as it will be saved. An edit carries the
// revision it was opened at, and the API refuses it (409) when someone else
// saved in between: the stale bar says who, and Save stays off until the
// newer version has been reviewed.
//
// Price / box (§7n.9) is shown only when the options say this caller holds
// the pricing capability (PRICE-1). An empty field saves the last price used
// for the product, which the placeholder and the hint name.

const errText = (e: any) => e?.body?.error || e?.message || String(e)

export function OrderEditor({
  user,
  rec,
  onCancel,
  onSaved,
  onReview,
}: {
  user: any
  // null for a new order
  rec: OrderRecord | null
  onCancel: () => void
  onSaved: (r: { id: string; reference: string; approved: boolean }) => void
  // leave the editor for the newer version (the stale bar's action)
  onReview: () => void
}) {
  const [opts, setOpts] = useState<EditorOptions | null>(null)
  const [ed, setEd] = useState<EditorState>(() => (rec ? editorFrom(rec) : blankEditor(todayIso())))
  const [errors, setErrors] = useState<string[]>([])
  const [stale, setStale] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [busyKind, setBusyKind] = useState<'save' | 'approve'>('save')
  const slow = useSlow(busy)

  const userRef = useRef(user)
  userRef.current = user
  useEffect(() => {
    apiJson<EditorOptions>(userRef.current, '/api/orders/options')
      .then(setOpts)
      .catch((e) => setErrors([`Failed to load the editor's choices: ${errText(e)}`]))
  }, [])

  // A layer (h 425-458): Escape and Cancel ask first once a value has changed.
  // The start is the editor once its choices have loaded. h 3984-3992: busy
  // prevents close, Cancel disabled while busy.
  const edEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged(ed, opts !== null)
  const { requestClose, confirm } = useLayer(true, onCancel, { dirty: dirty && !busy, el: edEl, focusFirst: opts !== null, busy })

  const set = (p: Partial<EditorState>) => setEd((s) => ({ ...s, ...p }))
  const setLine = (key: string, p: Partial<EditorLine>) => setEd((s) => ({ ...s, lines: s.lines.map((l) => (l.key === key ? { ...l, ...p } : l)) }))

  const customers = opts?.customers || []
  const customer = customers.find((c) => c.id === ed.customer_id) || null
  const approved = rec?.order.approval_status === 'approved'
  // Save & approve is for an existing order the caller may approve (D1.3):
  // never a new one (its author is its last material editor), and the server
  // refuses one whose save changes what the order says.
  const mayApprove = !!rec && !approved && (rec.approval?.may_approve ?? true)
  const valid = editorValid(ed)
  const pricing = !!opts?.pricing
  const grid = pricing ? os.edLinesGridPriced : os.edLinesGrid

  const pickCustomer = (id: string) => {
    const c = customers.find((z) => z.id === id)
    const first = c?.destinations.find((d) => d.offered)
    set({ customer_id: id, destination_id: first ? first.id : '' })
  }

  const save = async (approve: boolean) => {
    if (!valid || stale) return
    setBusy(true)
    setErrors([])
    try {
      if (rec) {
        const r = await apiJson<OrderRecord>(userRef.current, `/api/orders/${rec.order.id}`, {
          method: 'PUT',
          body: editorBody(ed, { revision: rec.order.revision, approve }, pricing),
        })
        onSaved({ id: r.order.id, reference: r.order.reference, approved: approve })
      } else {
        const r = await apiJson<{ order: { id: string; order_reference: string } }>(userRef.current, '/api/orders', {
          method: 'POST',
          body: editorBody(ed, { approve }, pricing),
        })
        onSaved({ id: r.order.id, reference: r.order.order_reference, approved: approve })
      }
    } catch (e: any) {
      if (e?.status === 409 && e.body?.stale) setStale(staleNote(e.body.by || 'another user', e.body.revision))
      else setErrors(Array.isArray(e?.body?.errors) && e.body.errors.length ? e.body.errors : [errText(e)])
    } finally {
      setBusy(false)
    }
  }

  const destOpts = customer ? withCurrent(customer.destinations, ed.destination_id) : []

  return (
    <div ref={edEl}>
      {stale && (
        <div className={os.staleBar} role="alert">
          <span>{stale}</span>
          <button type="button" className={os.staleBtn} onClick={onReview}>
            Review newer version
          </button>
        </div>
      )}
      {approved && <div className={os.materialNote}>{MATERIAL_NOTE}</div>}
      {errors.length > 0 && (
        <div className={styles.errList} role="alert" data-testid="editor-errors">
          {errors.map((x, i) => (
            <div key={i}>{x}</div>
          ))}
        </div>
      )}

      <div className={`${os.edCard} ${inertClass(busy, slow)}`}>
        <label className={os.edField}>
          <span className={styles.fieldLabel}>Customer</span>
          <select className={styles.input} value={ed.customer_id} onChange={(e) => pickCustomer(e.target.value)}>
            <option value="">Choose a customer</option>
            {withCurrent(customers, ed.customer_id).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className={os.edField}>
          <span className={styles.fieldLabel}>Delivery destination (one per order)</span>
          <select className={styles.input} value={ed.destination_id} onChange={(e) => set({ destination_id: e.target.value })}>
            <option value="">Choose a destination</option>
            {destOpts.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <label className={os.edField}>
          <span className={styles.fieldLabel}>Final destination (optional)</span>
          <input className={styles.input} value={ed.final} maxLength={120} placeholder="e.g. Shanghai" onChange={(e) => set({ final: e.target.value })} />
        </label>
        <label className={os.edField}>
          <span className={styles.fieldLabel}>Requested dispatch date (provisional if future)</span>
          <DateField className={styles.input} value={ed.date} onChange={(e) => set({ date: e.target.value })} />
        </label>
        <label className={os.edField}>
          <span className={styles.fieldLabel}>Customer PO / reference</span>
          <input className={styles.input} value={ed.po} onChange={(e) => set({ po: e.target.value })} />
        </label>
        <div className={os.edField}>
          <span className={styles.fieldLabel}>Transport mode</span>
          <div className={pa.tabTrack} role="radiogroup" aria-label="Transport mode" style={{ alignSelf: 'flex-start' }}>
            {MODES.map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={ed.mode === k} className={ed.mode === k ? pa.tabOn : pa.tab} onClick={() => set({ mode: k })}>
                {l}
              </button>
            ))}
          </div>
          <span className={styles.note}>{classText(customer?.kind, !!customer)}</span>
        </div>
        <label className={`${os.edField} ${styles.full}`}>
          <span className={styles.fieldLabel}>Internal instructions (not printed on customer documents)</span>
          <textarea className={os.textarea} value={ed.notes} onChange={(e) => set({ notes: e.target.value })} />
        </label>
      </div>

      <div className={pa.card} style={{ overflowX: 'auto', flex: 'none' }}>
        <div className={`${grid} ${styles.head}`}>
          <span>Variety</span>
          <span>Box / pack type</span>
          <span>Required lid / brand</span>
          <span>Boxes</span>
          {pricing && <span>Price / box</span>}
          <span>Source instructions (optional)</span>
          <span />
        </div>
        {ed.lines.map((l, i) => {
          const err = qtyError(l)
          const n = i + 1
          const last = pricing ? lastPriceFor(opts, l.variety, l.box_type) : null
          return (
            <div key={l.key} className={`${grid} ${os.edLine}`} data-testid="editor-line">
              <select className={styles.input} aria-label={`Line ${n} variety`} value={l.variety} onChange={(e) => setLine(l.key, withProduct(l, { variety: e.target.value }))}>
                <option value="">Variety</option>
                {namesWithCurrent(opts?.varieties || [], l.variety).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select className={styles.input} aria-label={`Line ${n} box type`} value={l.box_type} onChange={(e) => setLine(l.key, withProduct(l, { box_type: e.target.value }))}>
                <option value="">Box / pack type</option>
                {namesWithCurrent(opts?.box_types || [], l.box_type).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
              <select className={styles.input} aria-label={`Line ${n} required lid`} value={l.lid_id} onChange={(e) => setLine(l.key, { lid_id: e.target.value })}>
                <option value="">Lid not specified yet</option>
                {withCurrent(opts?.lids || [], l.lid_id).map((z) => (
                  <option key={z.value} value={z.value}>
                    {z.label}
                  </option>
                ))}
              </select>
              <div>
                <input
                  className={`${styles.input} ${styles.right}`}
                  aria-label={`Line ${n} boxes`}
                  inputMode="numeric"
                  value={l.qty}
                  onChange={(e) => setLine(l.key, { qty: digits(e.target.value) })}
                />
                {err && <div className={os.qtyErr}>{err}</div>}
              </div>
              {pricing && (
                <div>
                  <input
                    className={`${styles.input} ${styles.right}`}
                    aria-label={`Line ${n} price per box`}
                    inputMode="decimal"
                    placeholder={last == null ? '' : money(last).slice(1)}
                    value={l.price}
                    onChange={(e) => setLine(l.key, { price: e.target.value, priceTouched: true })}
                  />
                  {priceValid(l.price) ? (
                    <div className={os.priceHint}>{priceHint(l, last)}</div>
                  ) : (
                    <div className={os.qtyErr}>{PRICE_ERROR}</div>
                  )}
                </div>
              )}
              <input
                className={styles.input}
                aria-label={`Line ${n} source instructions`}
                placeholder="e.g. Patch 1 only"
                value={l.instructions}
                onChange={(e) => setLine(l.key, { instructions: e.target.value })}
              />
              {canRemoveLine(ed, l) ? (
                <button
                  type="button"
                  className={os.edRemove}
                  title="Remove line"
                  aria-label={`Remove line ${n}`}
                  onClick={() => setEd((s) => ({ ...s, lines: s.lines.filter((z) => z.key !== l.key) }))}
                >
                  ×
                </button>
              ) : (
                <span />
              )}
            </div>
          )
        })}
        <div className={os.edAdd}>
          <button type="button" className={pa.ghostBtn} onClick={() => setEd((s) => ({ ...s, lines: [...s.lines, blankLine()] }))}>
            Add product line
          </button>
        </div>
      </div>

      <div className={os.edActions}>
        <PendingButton className={pa.primaryBtn} busy={busy && busyKind === 'save'} pending="Saving…" disabled={!valid || !!stale || busy} onClick={() => { setBusyKind('save'); save(false) }}>
          Save draft
        </PendingButton>
        {mayApprove && (
          <PendingButton
            className={pa.ghostBtn}
            busy={busy && busyKind === 'approve'}
            pending="Saving…"
            disabled={!valid || !!stale || busy}
            title="Approves the order as it stands. A save that changes what the order says cannot also be your approval."
            onClick={() => { setBusyKind('approve'); save(true) }}
          >
            Save &amp; approve
          </PendingButton>
        )}
        <button type="button" className={pa.ghostBtn} disabled={busy} onClick={requestClose}>
          Cancel
        </button>
        {!valid && <span className={styles.note}>{VALID_NOTE}</span>}
      </div>
      {confirm}
    </div>
  )
}
